/* =============================================================
   OutgoingList.jsx — Outgoing students list (admin)
   -------------------------------------------------------------
   Displays all outgoing exchange students with search, filters
   (status, destination country, LA status), and pagination.
   Supports adding individual students and CSV import.
   Urgent students are highlighted with a red left border.

   TODO (backend integration):
   - Replace useStudentList hook data with Supabase query
   - Replace localStorage status overrides with a DB column
   ============================================================= */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useStudentList } from '../hooks/useStudentList';
import { useSemesters } from '../hooks/useSemesters';
import AddStudentModal from '../components/AddStudentModal';
import ImportStudentsModal from '../components/ImportStudentsModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

/* ─── STATUS BADGE STYLE ────────────────────────────────── */
// Dynamic: step labels are configured by the IO manager. Use emerald
// when the student is on the last step, indigo for any in-progress step.
function stepBadgeStyle(isCompleted) {
  return isCompleted
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-indigo-100 text-indigo-700';
}

/* ─── HELPERS ───────────────────────────────────────────── */

/* Avatar — deterministic colour from student name initials     */
function Avatar({ name }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['bg-indigo-500', 'bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-rose-500', 'bg-amber-500'];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div className={`w-8 h-8 ${colors[idx]} rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
}

/* exportCSV — downloads filtered rows as a CSV file            */
function exportCSV(rows) {
  const headers = ['Student Name', 'Destination University', 'Nationality', 'Status', 'Learning Agreement', 'Next Deadline'];
  const data = rows.map(s => [s.name, s.dest, s.country.replace(/[^\x00-\x7F]/g, '').trim(), s.status, s.la, s.deadline]);
  const csv  = [headers, ...data].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'outgoing_students_unixplore.csv'; a.click();
  URL.revokeObjectURL(url);
}

/* ─── OUTGOING LIST COMPONENT ───────────────────────────── */

export default function OutgoingList() {
  const navigate = useNavigate();

  const { students, addStudent, addStudents, deleteStudent, deleteAllStudents } = useStudentList('outgoing');
  const { semesters } = useSemesters();
  // Derive unique status labels from the live student list (covers all templates)
  const processSteps = [...new Set(students.map(s => s.status).filter(Boolean))].map(label => ({ id: label, label }));
  const [showModal,     setShowModal]     = useState(false);
  const [showImport,    setShowImport]    = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [uniFilter,    setUniFilter]    = useState('');
  const [docsFilter,   setDocsFilter]   = useState('');
  const [termFilter,   setTermFilter]   = useState('');
  const [page,         setPage]         = useState(1);

  const PER_PAGE = 5;

  const setFilter = (setter) => (val) => { setter(val); setPage(1); };

  const filtered = students.filter(s => {
    const searchOk = !search       || s.name.toLowerCase().includes(search.toLowerCase()) || s.dest.toLowerCase().includes(search.toLowerCase());
    const statusOk = !statusFilter || s.status === statusFilter;
    const uniOk    = !uniFilter    || s.dest === uniFilter;
    const docsOk   = !docsFilter   ||
      (docsFilter === 'Complete'    && s.docs === 'Complete') ||
      (docsFilter === 'Missing'     && s.docs.toLowerCase().includes('missing')) ||
      (docsFilter === 'Pending'     && s.docs === 'Pending') ||
      (docsFilter === 'Not started' && s.docs === 'Not started');
    const termOk   = !termFilter   || s.semesterId === termFilter;
    return searchOk && statusOk && uniOk && docsOk && termOk;
  });

  // Unique destination universities from actual student data, sorted alphabetically
  const uniOptions = [...new Set(students.map(s => s.dest).filter(Boolean))].sort();

  const hasFilters   = search || statusFilter || uniFilter || docsFilter || termFilter;
  const totalPages   = Math.ceil(filtered.length / PER_PAGE);
  const paginated    = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <Layout>
      {showModal && (
        <AddStudentModal
          type="outgoing"
          onAdd={(data) => { addStudent(data); setShowModal(false); }}
          onClose={() => setShowModal(false)}
        />
      )}
      {showImport && (
        <ImportStudentsModal
          type="outgoing"
          onImport={(rows) => { addStudents(rows); setShowImport(false); }}
          onClose={() => setShowImport(false)}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          title={`Delete ${deleteTarget.name}?`}
          message="This student will be permanently removed from the system, including all their documents and notes. This action cannot be undone."
          onConfirm={async () => {
            await deleteStudent(deleteTarget.id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      {showDeleteAll && (
        <DeleteConfirmModal
          title={`Delete all ${students.length} outgoing students?`}
          message="Every outgoing student and all their documents will be permanently removed. This action cannot be undone."
          onConfirm={async () => {
            await deleteAllStudents();
            setShowDeleteAll(false);
          }}
          onClose={() => setShowDeleteAll(false)}
        />
      )}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Outgoing Students</h1>
            <p className="text-[#464555] mt-1">
              {filtered.length !== students.length
                ? `${filtered.length} of ${students.length} students`
                : `${students.length} students this semester`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => exportCSV(filtered)} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <span className="material-symbols-outlined text-[16px]">download</span>
              Export CSV
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-4 py-2 border border-indigo-200 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">upload_file</span>
              Import Students
            </button>
            {students.length > 0 && (
              <button
                onClick={() => setShowDeleteAll(true)}
                className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-500 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">delete_sweep</span>
                Delete All
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#4343d5] text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]">add</span>
              New Student
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search students..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 text-slate-700 placeholder-slate-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setFilter(setStatusFilter)(e.target.value)}
            className="w-36 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
          >
            <option value="">All Statuses</option>
            {processSteps.map(s => <option key={s.id} value={s.label}>{s.label}</option>)}
          </select>
          <select
            value={uniFilter}
            onChange={e => setFilter(setUniFilter)(e.target.value)}
            className="w-44 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
          >
            <option value="">All Universities</option>
            {uniOptions.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <select
            value={docsFilter}
            onChange={e => setFilter(setDocsFilter)(e.target.value)}
            className="w-40 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
          >
            <option value="">All Documents</option>
            <option value="Complete">Complete</option>
            <option value="Missing">Missing</option>
            <option value="Pending">Pending</option>
            <option value="Not started">Not started</option>
          </select>
          <select
            value={termFilter}
            onChange={e => setFilter(setTermFilter)(e.target.value)}
            className="w-36 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
          >
            <option value="">All Terms</option>
            {semesters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setStatusFilter(''); setUniFilter(''); setDocsFilter(''); setTermFilter(''); setPage(1); }}
              className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[14px]">close</span>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-6 py-3.5">Student Name</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5 w-40">Destination University</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Nationality</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Status</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Term</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Documents</th>
              <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Next Deadline</th>
              <th className="px-4 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-slate-400">
                  No students match the selected filters.
                </td>
              </tr>
            ) : (
              paginated.map((s, i) => (
                <tr
                  key={i}
                  onClick={() => navigate(`/outgoing/${s.id}`)}
                  className="border-b border-slate-50 last:border-0 hover:bg-[#F0F2FF] transition-colors cursor-pointer group"
                >
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={s.name} />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#0b1c30]">{s.name}</span>
                        {s.isNew && (
                          <span className="text-[10px] font-bold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full">New</span>
                        )}
                        {s.urgent && (
                          <span className="flex items-center gap-0.5 text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
                            <span className="material-symbols-outlined text-[10px]">priority_high</span>
                            Urgent
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 w-40 max-w-[160px]">
                    <span className="block text-sm text-[#464555] truncate" title={s.dest}>{s.dest}</span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-[#464555]">{s.country}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${stepBadgeStyle(s.isCompleted)}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {s.semesterId
                      ? <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                          {semesters.find(t => t.id === s.semesterId)?.name ?? '—'}
                        </span>
                      : <span className="text-xs text-slate-300">—</span>
                    }
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-medium ${
                      s.docs === 'Complete'    ? 'text-emerald-600' :
                      s.docs === 'Not started' ? 'text-slate-400'   : 'text-amber-600'
                    }`}>{s.docs}</span>
                  </td>
                  <td className={`px-4 py-3.5 text-sm font-medium ${s.urgent ? 'text-red-600' : 'text-[#464555]'}`}>
                    {s.deadline}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="material-symbols-outlined text-slate-300 text-[18px] group-hover:text-indigo-400 transition-colors">arrow_forward</span>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteTarget(s); }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Delete student"
                      >
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {filtered.length === 0
            ? 'No students match the selected filters'
            : `Showing ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length} students`}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[16px]">chevron_left</span>
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                n === page ? 'bg-indigo-600 text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
          </button>
        </div>
        </div>
      </div>
    </Layout>
  );
}

